import React, { useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { A11y, Keyboard, Navigation, Pagination } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import clsx from 'clsx';

import { MContentHeader } from '../../molecules/MContentHeader';
import { MCarouselControls } from '../../molecules/MCarouselControls';
import { MCarouselControlsType } from '../../molecules/MCarouselControls/MCarouselControls.types';
import { AImage } from '../../atoms/AImage';
import { AspectRatio, MediaQuery, ResponsiveImageSpec, generateImageRenditions } from '../../utils/ImageUtils';

import {
  Container,
  TriptychWrapper,
  TriptychSlide,
  HeaderWrapper,
  ControlsContainer,
} from './OCarouselTriptych.styles';
import type { MCarouselControlsConfig, OCarouselTriptychProps } from './OCarouselTriptych.types';
import { OCarouselTriptychControlType, OCarouselTriptychImageOrientation } from './OCarouselTriptych.types';

import 'swiper/css';

const COMPONENT_NAME = 'o-web-ui-kit-OCarouselTriptych';
const TEST_ID = 'web-ui-kit-OCarouselTriptych';

const HORIZONTAL_IMAGE_SPECS: ResponsiveImageSpec[] = [
  { media: MediaQuery.MAX_WIDTH_SM, aspectRatio: AspectRatio.CLASSIC_HOR, imgWidthPx: 283 },
  { media: MediaQuery.MIN_WIDTH_MD, aspectRatio: AspectRatio.CLASSIC_HOR, imgWidthPx: 473 },
  { media: MediaQuery.MIN_WIDTH_XL, aspectRatio: AspectRatio.CLASSIC_HOR, imgWidthPx: 754 },
];

const VERTICAL_IMAGE_SPECS: ResponsiveImageSpec[] = [
  { media: MediaQuery.MAX_WIDTH_SM, aspectRatio: AspectRatio.CLASSIC_VER, imgWidthPx: 126 },
  { media: MediaQuery.MIN_WIDTH_MD, aspectRatio: AspectRatio.CLASSIC_VER, imgWidthPx: 210 },
  { media: MediaQuery.MIN_WIDTH_XL, aspectRatio: AspectRatio.CLASSIC_VER, imgWidthPx: 335 },
];

interface ResolvedControls {
  type: MCarouselControlsType;
  imageOverlay: boolean;
}

const isTruthy = (value: boolean | string | undefined): boolean => value === true || value === 'true';

export function resolveControls(config: MCarouselControlsConfig | undefined): ResolvedControls {
  if (!config) {
    return { type: MCarouselControlsType.Dots, imageOverlay: false };
  }

  const imageOverlay = isTruthy(config.imageOverlay);
  // AEM may send the empty string when the dropdown is unset; treat it the
  // same as `undefined` so we silently fall back to Dots without warning.
  const requestedType = config.type === ('' as unknown as typeof config.type) ? undefined : config.type;

  let type: MCarouselControlsType;
  switch (requestedType) {
    case OCarouselTriptychControlType.Dots:
    case undefined:
      type = MCarouselControlsType.Dots;
      break;
    case OCarouselTriptychControlType.Numbers:
    case OCarouselTriptychControlType.Digits:
      type = MCarouselControlsType.Numbers;
      break;
    default:
      if (process.env['NODE_ENV'] !== 'production') {
        console.warn(
          `[OCarouselTriptych] mCarouselControls.type "${requestedType}" is not supported. ` +
            `Allowed: "Dots", "Numbers" (or legacy "Digits"). Falling back to "Dots".`
        );
      }
      type = MCarouselControlsType.Dots;
  }

  return { type, imageOverlay };
}

export const OCarouselTriptych: React.FC<OCarouselTriptychProps> = ({
  images,
  onSlideChange,
  onSlideClick,
  spaceBetween = 16,
  imageOrientation = OCarouselTriptychImageOrientation.Horizontal,
  mContentHeader,
  mCarouselControls,
  swiperOptions = {},
  className,
}) => {
  const isVertical = imageOrientation === OCarouselTriptychImageOrientation.Vertical;
  const responsiveImageSpecs = isVertical ? VERTICAL_IMAGE_SPECS : HORIZONTAL_IMAGE_SPECS;
  const resolvedControls = resolveControls(mCarouselControls);

  const swiperRef = useRef<SwiperType | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [totalSlides, setTotalSlides] = useState(images.length);

  const handleSwiperInit = (swiper: SwiperType) => {
    swiperRef.current = swiper;
    setTotalSlides(swiper.slides.length);
  };

  const handleSlideChange = (swiper: SwiperType) => {
    const newIndex = swiper.params?.loop ? swiper.realIndex : swiper.activeIndex;
    setCurrentSlide(newIndex);
    onSlideChange?.(newIndex);
  };

  const handlePrev = () => swiperRef.current?.slidePrev();
  const handleNext = () => swiperRef.current?.slideNext();

  const carouselAriaLabel = mContentHeader?.mTextBlockSimple?.aHeader?.text || 'Image carousel';

  const enableLoop = images.length > 1;
  const defaultSwiperOptions = {
    spaceBetween,
    centeredSlides: true,
    slidesPerView: 'auto' as const,
    loop: enableLoop,
    keyboard: { enabled: true },
  };

  const finalSwiperOptions = { ...defaultSwiperOptions, ...swiperOptions };

  return (
    <Container
      data-component-name={COMPONENT_NAME}
      data-testid={TEST_ID}
      className={clsx('m-container-fullbleed', className)}
      aria-label={carouselAriaLabel}
    >
      <HeaderWrapper>
        <MContentHeader {...mContentHeader} />
      </HeaderWrapper>
      <TriptychWrapper $orientation={imageOrientation}>
        <Swiper
          modules={[A11y, Keyboard, Navigation, Pagination]}
          onSwiper={handleSwiperInit}
          onSlideChange={handleSlideChange}
          {...finalSwiperOptions}
        >
          {images.map((imageProps, index) => (
            <SwiperSlide key={[imageProps.image?.assetPath || imageProps.src, index].join('-')}>
              <TriptychSlide onClick={() => onSlideClick?.(index)}>
                <AImage
                  {...imageProps}
                  renditions={generateImageRenditions(responsiveImageSpecs, imageProps.image?.assetPath)}
                />
              </TriptychSlide>
            </SwiperSlide>
          ))}
        </Swiper>
        <ControlsContainer $orientation={imageOrientation}>
          <MCarouselControls
            type={resolvedControls.type}
            imageOverlay={resolvedControls.imageOverlay}
            totalSlides={totalSlides}
            currentSlide={currentSlide}
            onPrev={handlePrev}
            onNext={handleNext}
            loop={enableLoop}
          />
        </ControlsContainer>
      </TriptychWrapper>
    </Container>
  );
};

export * from './OCarouselTriptych.types';
